import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import CarHirePurchaseSummaryLocation from 'components/Pages/MyEscapesPage/PurchaseSummary/Common/CarHirePurchaseSummary/CarHirePurchaseSummaryLocation'
import TextButton from 'LuxKit/Button/TextButton'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import useCarHireReservationInfo from 'hooks/CarHire/useCarHireReservationInfo'
import { canRefundCarHireItem } from 'lib/order/orderItemUtils'

interface Props {
  order: App.Order
  isPastOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}

export default function LesaCarHirePurchaseSummary(props: Props) {
  const intl = useIntl()
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap, canEdit } = props

  const dispatch = useAppDispatch()

  const item = order.carHireItems[0]
  const reservation = item?.reservation

  const sameLocation = reservation?.pickUp?.locationName === reservation?.dropOff?.locationName

  const carHireReservation = useCarHireReservationInfo(item.idReservation ?? '', order.purchasingCustomerId)
  const reservationInfo = carHireReservation.reservation

  const canCarHireBeCancelled = reservationInfo ? canRefundCarHireItem(item, order, reservationInfo.cancellationPolicy) : false
  const carHireFreeCancellationPeriodEndDate = reservationInfo?.cancellationPolicy?.maxDate

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', {
      action: 'selectOrder',
      payload: {
        canCarHireBeCancelled,
        carHireFreeCancellationPeriodEndDate,
        id_orders: order.id,
        canEditOrder: canEdit,
      },
    }))
  }, [canCarHireBeCancelled, carHireFreeCancellationPeriodEndDate, dispatch, order.id, canEdit])

  if (!reservation?.vehicle) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions', 'showFinePrint', 'changeRoom']))

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader order={order}>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineCarIcon size="XS" />}>{intl.formatMessage({
              id: 'wxdPmeTU_uzZaJE6ecdM',
              defaultMessage: 'Car hire',
              description: 'Category label for car hire purchase summary in car hire section',
            })}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
              id: 'AXGCSp5kSpvS2af-bRQn',
              defaultMessage: 'Upcoming',
              description: 'Status label for upcoming car hire booking in the booking flow',
            })}
            </StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
              id: 'na10O8H_sP44gUK0rvZ6',
              defaultMessage: 'Past',
              description: 'Status label for past car hire booking in the booking or order summary view',
            })}
            </StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <Group direction="horizontal" verticalAlign="end" gap={4} wrap="wrap">
                <LesaPurchaseSummaryTitle orderId={order.id}>
                  {reservation?.vehicle?.model}
                </LesaPurchaseSummaryTitle>
                {!reservation?.vehicle?.guarenteedModel && <BodyText wrap="no-wrap" as="span" variant="medium" colour="neutral-three">{intl.formatMessage({
                  id: 'mjNXxq1kMNCCjV3tcE-f',
                  defaultMessage: 'or similar',
                  description: 'Text indicating the vehicle model may vary in the car hire section',
                })}
                </BodyText>}
              </Group>
              <BodyTextBlock variant="small" colour="neutral-three">
                <CarHirePurchaseSummaryLocation
                  sameLocation={sameLocation}
                  pickUpLocationName={reservation?.pickUp?.locationName}
                  dropOffLocationName={reservation?.dropOff?.locationName}
                  order={order}
                />
              </BodyTextBlock>
              {order.bookingNumber && <BodyTextBlock variant="small" colour="neutral-two"><strong>{intl.formatMessage({
                id: 'pkWGj02H35DE7eMGQTEy',
                defaultMessage: 'Booking ID:',
                description: 'Label for the car hire booking ID in the booking or order summary view',
              })}
              </strong> {order.bookingNumber}
              </BodyTextBlock>}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                {intl.formatMessage({
                  id: 'v7kxrJD4-ucaQhMsJ-dr',
                  defaultMessage: 'Select',
                  description: 'Button text to select a car hire booking in the booking flow',
                })}
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}
